import { useEffect, useMemo, useState } from 'react'

import GShell from './gg/GShell.jsx'
import { PbccSectionTitle } from '../components/PbccLayout.jsx'
import AIAssistant from '../components/AIAssistant.jsx'
import CLUB from '../config/club.js'
import {
  watchProShopCredit,
  createAccount, deleteAccount,
  addTransaction, updateTransaction, deleteTransaction,
  createGiftCard, addGiftCardRedemption, deleteGiftCardRedemption, deleteGiftCard,
} from '../lib/proShopCredit.js'

const PRIMARY = CLUB.primaryColor || 'var(--ge-primary)'
const ACCENT = CLUB.accentColor || 'var(--ge-primary)'
const today = () => new Date().toISOString().slice(0, 10)
const money = (n) => `$${(Number(n) || 0).toFixed(2)}`
const fmtDate = (iso) => {
  const d = iso ? new Date(`${iso}T00:00`) : null
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : (iso || '—')
}

const txSum = (transactions) =>
  Object.values(transactions || {}).reduce(
    (b, t) => b + (t?.type === 'redeem' ? -Number(t.amount || 0) : Number(t.amount || 0)), 0)

const cardRemaining = (card) =>
  Number(card?.value || 0) - Object.values(card?.redemptions || {}).reduce((s, r) => s + Number(r?.amount || 0), 0)

export default function ProShopCredit() {
  const [data, setData] = useState({})
  const [selAccountId, setSelAccountId] = useState(null)
  const [selCardId, setSelCardId] = useState(null)

  useEffect(() => { const off = watchProShopCredit(setData); return () => off() }, [])

  const accounts = useMemo(() =>
    Object.entries(data.accounts || {})
      .map(([id, v]) => ({ id, name: v?.name || '(unnamed)', createdAt: v?.createdAt || 0, transactions: v?.transactions || {}, balance: txSum(v?.transactions) }))
      .sort((a, b) => a.name.localeCompare(b.name)),
  [data])

  const cards = useMemo(() =>
    Object.entries(data.giftCards || {})
      .map(([id, v]) => ({ id, code: v?.code || '', value: Number(v?.value || 0), recipient: v?.recipient || null, redemptions: v?.redemptions || {}, remaining: cardRemaining(v), createdAt: v?.createdAt || 0 }))
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)),
  [data])

  const selAccount = accounts.find((a) => a.id === selAccountId) || null
  const selCard = cards.find((c) => c.id === selCardId) || null

  return (
    <GShell>
              {/* Credit accounts */}
              <section>
                <PbccSectionTitle title="Pro Shop Credit" subtitle="A shop-controlled ledger — every balance is the sum of its transactions, so edits recalculate automatically." />
                <div className="mt-3 grid md:grid-cols-2 gap-3">
                  <NewAccountForm />
                  <IssueWinningsForm accounts={accounts} />
                </div>
                <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {accounts.length === 0 ? (
                    <p className="text-sm text-gray-500">No credit accounts yet. Add one above.</p>
                  ) : accounts.map((a) => (
                    <button key={a.id} type="button" onClick={() => setSelAccountId(a.id)}
                      className="text-left rounded-2xl p-4 transition active:scale-[0.99] hover:shadow-lg"
                      style={{ background: CLUB.creamColor, border: `1px solid ${ACCENT}30`, boxShadow: '0 6px 16px rgba(0,0,0,0.06)' }}>
                      <div className="text-base font-extrabold" style={{ color: PRIMARY }}>{a.name}</div>
                      <div className="text-2xl font-extrabold mt-1" style={{ color: a.balance > 0 ? '#15803D' : PRIMARY }}>{money(a.balance)}</div>
                      <div className="text-[11px] text-gray-500 mt-1">{Object.keys(a.transactions).length} transaction{Object.keys(a.transactions).length === 1 ? '' : 's'} · View →</div>
                    </button>
                  ))}
                </div>
              </section>

              {/* Gift cards */}
              <section className="pb-4">
                <PbccSectionTitle title="Gift Cards & Certificates" subtitle="Track each card down to a zero balance." />
                <div className="mt-3"><NewGiftCardForm /></div>
                <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {cards.length === 0 ? (
                    <p className="text-sm text-gray-500">No gift cards yet. Create one above.</p>
                  ) : cards.map((c) => {
                    const redeemed = c.remaining <= 0.0001
                    return (
                      <button key={c.id} type="button" onClick={() => setSelCardId(c.id)}
                        className="text-left rounded-2xl p-4 transition active:scale-[0.99] hover:shadow-lg"
                        style={{ background: CLUB.creamColor, border: `1px solid ${ACCENT}30`, boxShadow: '0 6px 16px rgba(0,0,0,0.06)', opacity: redeemed ? 0.7 : 1 }}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono font-extrabold text-sm" style={{ color: PRIMARY }}>{c.code}</span>
                          <StatusPill redeemed={redeemed} />
                        </div>
                        <div className="text-2xl font-extrabold mt-1" style={{ color: redeemed ? '#6B7280' : '#15803D' }}>{money(c.remaining)}</div>
                        <div className="text-[11px] text-gray-500 mt-1">of {money(c.value)}{c.recipient ? ` · ${c.recipient}` : ''} · View →</div>
                      </button>
                    )
                  })}
                </div>
              </section>

      {selAccount ? <AccountModal account={selAccount} onClose={() => setSelAccountId(null)} /> : null}
      {selCard ? <GiftCardModal card={selCard} onClose={() => setSelCardId(null)} /> : null}
      <AIAssistant tournamentId={null} />
    </GShell>
  )
}

// ── Forms ────────────────────────────────────────────────────────────────
function NewAccountForm() {
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const submit = async () => {
    setBusy(true); setErr('')
    try { await createAccount(name); setName('') }
    catch (e) { setErr(e?.message || 'Could not add account.') }
    finally { setBusy(false) }
  }
  return (
    <Card title="New Credit Account">
      <div className="flex gap-2">
        <input className="input flex-1" placeholder="Member / player name" value={name}
          onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && name.trim() && submit()} />
        <button type="button" className="btn-primary text-sm" onClick={submit} disabled={busy || !name.trim()}>{busy ? '…' : 'Add'}</button>
      </div>
      <p className="text-[11px] text-gray-400 mt-1">Later these can link to registered players.</p>
      {err ? <Err msg={err} /> : null}
    </Card>
  )
}

function IssueWinningsForm({ accounts }) {
  const [accountId, setAccountId] = useState('')
  const [event, setEvent] = useState('')
  const [amount, setAmount] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [ok, setOk] = useState('')
  const submit = async () => {
    setBusy(true); setErr(''); setOk('')
    try {
      await addTransaction(accountId, { type: 'credit', amount, reason: `${event.trim() || 'Tournament'} winnings`, date: today() })
      setOk('Winnings issued ✓'); setAmount(''); setEvent('')
    } catch (e) { setErr(e?.message || 'Could not issue winnings.') }
    finally { setBusy(false) }
  }
  return (
    <Card title="Issue Tournament Winnings">
      <div className="grid grid-cols-2 gap-2">
        <select className="input col-span-2" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
          <option value="">Choose account…</option>
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <input className="input" placeholder="Event name" value={event} onChange={(e) => setEvent(e.target.value)} />
        <input className="input" type="number" step="0.01" min="0" placeholder="$ amount" value={amount} onChange={(e) => setAmount(e.target.value)} />
      </div>
      <button type="button" className="btn-primary text-sm mt-2 w-full" onClick={submit} disabled={busy || !accountId || !amount}>
        {busy ? 'Issuing…' : '🏆 Issue Winnings'}
      </button>
      <p className="text-[11px] text-gray-400 mt-1">Drops a credit reading "[Event] winnings." Manual for now — wires into brackets later.</p>
      {ok ? <p className="text-xs font-bold mt-1" style={{ color: '#15803D' }}>{ok}</p> : null}
      {err ? <Err msg={err} /> : null}
    </Card>
  )
}

function NewGiftCardForm() {
  const [code, setCode] = useState('')
  const [value, setValue] = useState('')
  const [recipient, setRecipient] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const submit = async () => {
    setBusy(true); setErr('')
    try { await createGiftCard({ code, value, recipient }); setCode(''); setValue(''); setRecipient('') }
    catch (e) { setErr(e?.message || 'Could not create card.') }
    finally { setBusy(false) }
  }
  return (
    <Card title="New Gift Card / Certificate">
      <div className="grid sm:grid-cols-3 gap-2">
        <input className="input font-mono" placeholder="Code (e.g. PBCC-2026-001)" value={code} onChange={(e) => setCode(e.target.value)} />
        <input className="input" type="number" step="0.01" min="0" placeholder="$ value" value={value} onChange={(e) => setValue(e.target.value)} />
        <input className="input" placeholder="Recipient (optional)" value={recipient} onChange={(e) => setRecipient(e.target.value)} />
      </div>
      <button type="button" className="btn-primary text-sm mt-2" onClick={submit} disabled={busy || !code.trim() || !value}>{busy ? 'Creating…' : 'Create Card'}</button>
      {err ? <Err msg={err} /> : null}
    </Card>
  )
}

// ── Account modal (transactions + history) ───────────────────────────────
function AccountModal({ account, onClose }) {
  const [err, setErr] = useState('')
  // Running-balance history: oldest → newest, cumulative.
  const history = useMemo(() => {
    const rows = Object.entries(account.transactions || {})
      .map(([id, t]) => ({ id, ...t }))
      .sort((a, b) => (a.date || '').localeCompare(b.date || '') || (a.createdAt || 0) - (b.createdAt || 0))
    let run = 0
    return rows.map((r) => { run += r.type === 'redeem' ? -Number(r.amount || 0) : Number(r.amount || 0); return { ...r, running: run } })
  }, [account.transactions])

  const onDeleteAccount = async () => {
    if (!window.confirm(`Delete "${account.name}" and all its transactions? This cannot be undone.`)) return
    try { await deleteAccount(account.id); onClose() } catch (e) { setErr(e?.message || 'Delete failed.') }
  }

  return (
    <Modal title={account.name} onClose={onClose} size="xl">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-gray-600">Balance</div>
        <div className="text-2xl font-extrabold" style={{ color: account.balance > 0 ? '#15803D' : PRIMARY }}>{money(account.balance)}</div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <TxForm accountId={account.id} kind="credit" />
        <TxForm accountId={account.id} kind="redeem" />
      </div>

      <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${ACCENT}33` }}>
        <div className="grid grid-cols-[auto_1fr_auto_auto] gap-2 px-3 py-2 text-[10px] font-extrabold uppercase tracking-widest" style={{ background: 'var(--ge-primary)', color: ACCENT }}>
          <span>Date</span><span>Reason</span><span className="text-right">Amount</span><span className="text-right">Balance</span>
        </div>
        {history.length === 0 ? (
          <div className="px-3 py-3 text-sm text-gray-500 bg-white">No transactions yet.</div>
        ) : [...history].reverse().map((r) => <TxRow key={r.id} accountId={account.id} r={r} />)}
      </div>

      <div className="flex justify-end mt-4">
        <button type="button" className="btn-ghost text-sm" style={{ color: '#B91C1C' }} onClick={onDeleteAccount}>🗑 Delete Account</button>
      </div>
      {err ? <Err msg={err} /> : null}
    </Modal>
  )
}

function TxForm({ accountId, kind }) {
  const isCredit = kind === 'credit'
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [date, setDate] = useState(today())
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const submit = async () => {
    setBusy(true); setErr('')
    try {
      await addTransaction(accountId, { type: kind, amount, reason, date })
      setAmount(''); setReason('')
    } catch (e) { setErr(e?.message || 'Could not save.') }
    finally { setBusy(false) }
  }
  return (
    <div className="rounded-xl p-3" style={{ background: CLUB.creamColor, border: `1px solid ${ACCENT}30` }}>
      <div className="text-xs font-extrabold uppercase tracking-widest mb-2" style={{ color: isCredit ? '#15803D' : '#B91C1C' }}>
        {isCredit ? '➕ Add Credit' : '➖ Redeem / Deduct'}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input className="input" type="number" step="0.01" min="0" placeholder="$ amount" value={amount} onChange={(e) => setAmount(e.target.value)} />
        <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </div>
      <input className="input mt-2 w-full" placeholder={isCredit ? 'Reason (required) — e.g. Four-Ball winnings' : 'Note (optional)'} value={reason} onChange={(e) => setReason(e.target.value)} />
      <button type="button" className="btn-primary text-sm mt-2 w-full" onClick={submit} disabled={busy || !amount || (isCredit && !reason.trim())}>
        {busy ? '…' : isCredit ? 'Add Credit' : 'Redeem'}
      </button>
      {err ? <Err msg={err} /> : null}
    </div>
  )
}

function TxRow({ accountId, r }) {
  const [editing, setEditing] = useState(false)
  const [amount, setAmount] = useState(String(r.amount))
  const [reason, setReason] = useState(r.reason || '')
  const [date, setDate] = useState(r.date || today())
  const [err, setErr] = useState('')
  const isCredit = r.type !== 'redeem'
  const save = async () => {
    try { await updateTransaction(accountId, r.id, { amount, reason, date }); setEditing(false) }
    catch (e) { setErr(e?.message || 'Update failed.') }
  }
  const del = async () => {
    if (!window.confirm('Delete this transaction?')) return
    try { await deleteTransaction(accountId, r.id) } catch (e) { setErr(e?.message || 'Delete failed.') }
  }
  if (editing) {
    return (
      <div className="px-3 py-2 bg-white border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
        <div className="grid grid-cols-2 gap-2">
          <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          <input className="input" type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </div>
        <input className="input mt-2 w-full" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason / note" />
        <div className="flex gap-2 justify-end mt-2">
          <button className="btn-ghost text-xs" onClick={() => setEditing(false)}>Cancel</button>
          <button className="btn-primary text-xs" onClick={save} disabled={!amount || (isCredit && !reason.trim())}>Save</button>
        </div>
        {err ? <Err msg={err} /> : null}
      </div>
    )
  }
  return (
    <div className="grid grid-cols-[auto_1fr_auto_auto] gap-2 items-center px-3 py-2 bg-white border-t text-sm" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
      <span className="text-xs text-gray-500 whitespace-nowrap">{fmtDate(r.date)}</span>
      <span className="min-w-0">
        <span className="text-gray-800">{r.reason || (isCredit ? 'Credit' : 'Redemption')}</span>
        <button onClick={() => setEditing(true)} className="ml-2 text-[11px]" style={{ color: ACCENT }}>edit</button>
        <button onClick={del} className="ml-2 text-[11px] text-red-600">delete</button>
      </span>
      <span className="text-right font-bold whitespace-nowrap" style={{ color: isCredit ? '#15803D' : '#B91C1C' }}>{isCredit ? '+' : '−'}{money(r.amount)}</span>
      <span className="text-right font-extrabold whitespace-nowrap" style={{ color: PRIMARY }}>{money(r.running)}</span>
    </div>
  )
}

// ── Gift card modal ──────────────────────────────────────────────────────
function GiftCardModal({ card, onClose }) {
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const [date, setDate] = useState(today())
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const redeemed = card.remaining <= 0.0001
  const reds = Object.entries(card.redemptions || {})
    .map(([id, v]) => ({ id, ...v }))
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))

  const addRed = async () => {
    setBusy(true); setErr('')
    try {
      if (Number(amount) > card.remaining + 0.0001) throw new Error(`Only ${money(card.remaining)} remaining on this card.`)
      await addGiftCardRedemption(card.id, { amount, note, date })
      setAmount(''); setNote('')
    } catch (e) { setErr(e?.message || 'Could not redeem.') }
    finally { setBusy(false) }
  }
  const delRed = async (redId) => { try { await deleteGiftCardRedemption(card.id, redId) } catch (e) { setErr(e?.message || 'Delete failed.') } }
  const delCard = async () => {
    if (!window.confirm(`Delete card ${card.code}? This cannot be undone.`)) return
    try { await deleteGiftCard(card.id); onClose() } catch (e) { setErr(e?.message || 'Delete failed.') }
  }

  return (
    <Modal title={`Gift Card ${card.code}`} onClose={onClose} size="lg">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm text-gray-600">Remaining {card.recipient ? `· ${card.recipient}` : ''}</div>
          <div className="text-2xl font-extrabold" style={{ color: redeemed ? '#6B7280' : '#15803D' }}>{money(card.remaining)} <span className="text-sm text-gray-400 font-semibold">/ {money(card.value)}</span></div>
        </div>
        <StatusPill redeemed={redeemed} />
      </div>

      {!redeemed ? (
        <div className="rounded-xl p-3 mb-3" style={{ background: CLUB.creamColor, border: `1px solid ${ACCENT}30` }}>
          <div className="grid grid-cols-2 gap-2">
            <input className="input" type="number" step="0.01" min="0" placeholder="$ redeem" value={amount} onChange={(e) => setAmount(e.target.value)} />
            <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <input className="input mt-2 w-full" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
          <button type="button" className="btn-primary text-sm mt-2 w-full" onClick={addRed} disabled={busy || !amount}>{busy ? '…' : 'Redeem Against Card'}</button>
        </div>
      ) : (
        <div className="rounded-lg px-3 py-2 mb-3 text-sm font-bold text-center" style={{ background: 'rgba(0,0,0,0.05)', color: '#6B7280' }}>Fully redeemed — $0.00 remaining.</div>
      )}

      <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${ACCENT}33` }}>
        <div className="px-3 py-2 text-[10px] font-extrabold uppercase tracking-widest" style={{ background: 'var(--ge-primary)', color: ACCENT }}>Redemptions</div>
        {reds.length === 0 ? (
          <div className="px-3 py-3 text-sm text-gray-500 bg-white">No redemptions yet.</div>
        ) : reds.map((r) => (
          <div key={r.id} className="flex items-center gap-2 px-3 py-2 bg-white border-t text-sm" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
            <span className="text-xs text-gray-500 whitespace-nowrap">{fmtDate(r.date)}</span>
            <span className="flex-1 min-w-0 text-gray-800 truncate">{r.note || 'Redemption'}</span>
            <span className="font-bold whitespace-nowrap" style={{ color: '#B91C1C' }}>−{money(r.amount)}</span>
            <button onClick={() => delRed(r.id)} className="text-[11px] text-red-600">delete</button>
          </div>
        ))}
      </div>

      <div className="flex justify-end mt-4">
        <button type="button" className="btn-ghost text-sm" style={{ color: '#B91C1C' }} onClick={delCard}>🗑 Delete Card</button>
      </div>
      {err ? <Err msg={err} /> : null}
    </Modal>
  )
}

// ── Small shared UI ──────────────────────────────────────────────────────
function Card({ title, children }) {
  return (
    <div className="rounded-2xl p-4" style={{ background: CLUB.creamColor, border: `1px solid ${ACCENT}30`, boxShadow: '0 6px 16px rgba(0,0,0,0.06)' }}>
      <div className="text-xs font-extrabold uppercase tracking-widest mb-2" style={{ color: ACCENT }}>{title}</div>
      {children}
    </div>
  )
}

function StatusPill({ redeemed }) {
  return (
    <span className="text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full"
      style={redeemed
        ? { background: 'rgba(0,0,0,0.06)', color: '#6B7280' }
        : { background: 'rgba(34,168,90,0.12)', color: '#15803D', border: '1px solid #15803D55' }}>
      {redeemed ? 'Redeemed' : 'Active'}
    </span>
  )
}

function Err({ msg }) {
  return <p className="text-xs font-bold mt-1" style={{ color: '#B91C1C' }}>{msg}</p>
}

function Modal({ title, onClose, children, size = 'lg' }) {
  const maxW = size === 'xl' ? 'max-w-3xl' : size === 'sm' ? 'max-w-md' : 'max-w-xl'
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" style={{ background: 'rgba(var(--ge-primary-rgb),0.78)' }} onClick={onClose}>
      <div className={`w-full ${maxW} rounded-2xl my-8`} style={{ background: CLUB.creamColor, border: `1px solid ${ACCENT}44`, boxShadow: '0 24px 60px rgba(0,0,0,0.45)' }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: `1px solid ${ACCENT}33` }}>
          <h3 className="text-lg font-extrabold" style={{ color: PRIMARY, fontFamily: 'Cinzel, serif' }}>{title}</h3>
          <button type="button" onClick={onClose} className="text-2xl leading-none" style={{ color: PRIMARY }} aria-label="Close">×</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  )
}
